import type {
  AppointmentDocumentationSnapshotResponse,
  AppointmentDocumentationStreamEvent,
  AppointmentDraftTextStreamEvent,
} from "../../../shared/api-types";
import type { AppointmentEnhancementProgressSignal } from "./appointment-documentation-state";

/**
 * NO-772: the workspace's reader for the content-free Enhancement progress
 * stream. The stream only marks "Writing" and asks for an early durable
 * re-read; the store's polling interval keeps working without it.
 */
export const PROGRESS_RECONNECT_BASE_MS = 1_000;
export const PROGRESS_RECONNECT_MAX_MS = 15_000;

/** Fixed message only: stream errors never carry line text. */
export class DocumentationStreamError extends Error {
  constructor() {
    super("The documentation stream could not be read.");
    this.name = "DocumentationStreamError";
  }
}

/**
 * The generation to follow: only while the flag is on and the durable
 * snapshot shows a claimed attempt, or (NO-785) a running Re-enhance, with a
 * known generation.
 */
export function enhancementProgressTarget(
  enabled: boolean,
  snapshot: AppointmentDocumentationSnapshotResponse | null
): string | null {
  if (!enabled || !snapshot) return null;

  const isRunning =
    snapshot.enhancementState === "claimed" ||
    snapshot.reEnhancementState === "running";

  return isRunning ? snapshot.generationId : null;
}

/**
 * NO-785: only the first Enhance streams draft text into the locked preview.
 * A Re-enhance follows the content-free progress stream alone, because its
 * output never shows until it lands as the new head.
 */
export function followsDraftText(
  snapshot: AppointmentDocumentationSnapshotResponse | null
): boolean {
  return snapshot?.enhancementState === "claimed";
}

type StreamLine = AppointmentDocumentationStreamEvent | AppointmentDraftTextStreamEvent;
export type DraftTextLine = Extract<AppointmentDraftTextStreamEvent, { type: "text" }>;

/** NO-772 scope change: a text line is valid only on the draft-text stream. */
function parseText(event: Record<string, unknown>): DraftTextLine | null {
  const { generationId, runId, sequence, textDelta } = event;
  const ids = typeof generationId === "string" && typeof runId === "string";
  const valid = ids && typeof textDelta === "string" && Number.isInteger(sequence);

  return valid
    ? { type: "text", generationId, runId, sequence: sequence as number, textDelta }
    : null;
}

function parseLine(line: string, allowText = false): StreamLine {
  let value: unknown;

  try {
    value = JSON.parse(line);
  } catch {
    throw new DocumentationStreamError();
  }

  const event = (value ?? {}) as Record<string, unknown>;

  if (event.type === "heartbeat" || event.type === "settled")
    return { type: event.type };

  if (event.type === "writing" && typeof event.generationId === "string") {
    return { type: "writing", generationId: event.generationId };
  }

  const text = allowText && event.type === "text" ? parseText(event) : null;
  if (text) return text;

  throw new DocumentationStreamError();
}

/** NDJSON stream events, validated one line at a time; fails closed. */
export async function* readAppointmentDocumentationStream(
  body: ReadableStream<Uint8Array> | null,
  allowText = false
): AsyncGenerator<StreamLine> {
  if (!body) throw new DocumentationStreamError();

  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    for (;;) {
      const { done, value } = await reader.read();
      buffer += decoder.decode(value, { stream: !done });
      const lines = buffer.split("\n");
      buffer = done ? "" : (lines.pop() ?? "");

      for (const line of lines) if (line.trim()) yield parseLine(line, allowText);

      if (done) return;
    }
  } finally {
    reader.releaseLock();
  }
}

export type DocumentationStreamRequest = (
  path: string,
  init: RequestInit
) => Promise<Response>;

export interface EnhancementProgressStreamOptions {
  appointmentId: string;
  request: DocumentationStreamRequest;
  onSignal: (signal: AppointmentEnhancementProgressSignal) => void;
  /**
   * NO-772 scope change: follow the clinical-read draft-text route instead,
   * passing each text line here. Absent, the content-free progress route.
   */
  onText?: (line: DraftTextLine) => void;
  schedule?: (run: () => void, delay: number) => ReturnType<typeof setTimeout>;
  cancel?: (timer: ReturnType<typeof setTimeout>) => void;
  /** Jitter source in [0, 1); injected by tests. */
  random?: () => number;
}

type StreamEnd = "settled" | "refused" | "dropped";

async function followOnce(
  options: EnhancementProgressStreamOptions,
  signal: AbortSignal,
  opened: () => void
): Promise<StreamEnd> {
  const route = options.onText ? "draft/stream" : "documentation/stream";
  const path = `/api/appointments/${encodeURIComponent(options.appointmentId)}/${route}`;
  const response = await options.request(path, { cache: "no-store", signal });
  if (!response.ok) return "refused";

  const lines = readAppointmentDocumentationStream(response.body, !!options.onText);

  for await (const event of lines) {
    if (signal.aborted) return "dropped";

    // A stream that delivered a line was healthy; backoff starts over.
    opened();

    if (event.type === "settled") {
      options.onSignal({ type: "settled" });

      return "settled";
    }

    if (event.type === "writing") options.onSignal(event);

    if (event.type === "text") options.onText?.(event);
  }

  return "dropped";
}

/** Equal jitter: half the doubled delay, plus up to half again. */
function reconnectDelay(attempt: number, random: () => number): number {
  const ceiling = Math.min(
    PROGRESS_RECONNECT_MAX_MS,
    PROGRESS_RECONNECT_BASE_MS * 2 ** attempt
  );

  return Math.round(ceiling / 2 + (ceiling / 2) * random());
}

/**
 * Follows one Appointment's running Enhancement until the server says
 * `settled`, reconnecting with jittered backoff after any other end. A drop
 * keeps "Writing": output did begin, and the replay restores it on reconnect.
 * A refused response (flag off, no access) stops without retrying. Returns
 * `close`.
 */
export function openEnhancementProgressStream(
  options: EnhancementProgressStreamOptions
): () => void {
  const aborter = new AbortController();
  const schedule = options.schedule ?? ((run, delay) => setTimeout(run, delay));
  const cancel = options.cancel ?? ((timer) => clearTimeout(timer));
  const random = options.random ?? Math.random;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let attempt = 0;

  async function connect(): Promise<void> {
    timer = null;
    let end: StreamEnd = "dropped";

    try {
      end = await followOnce(options, aborter.signal, () => {
        attempt = 0;
      });
    } catch {
      end = "dropped";
    }

    if (aborter.signal.aborted || end !== "dropped") return;

    timer = schedule(() => void connect(), reconnectDelay(attempt, random));
    attempt += 1;
  }

  void connect();

  return () => {
    aborter.abort();
    if (timer) cancel(timer);

    timer = null;
  };
}
